import {
  Bot,
  CircleAlert,
  Crosshair,
  ExternalLink,
  Fingerprint,
  ShieldCheck,
  UserCheck,
  Wrench,
} from "lucide-react";
import { type FormEvent, useEffect, useId, useState } from "react";
import { api } from "../../api/client.js";
import { displayRuleId } from "../../app/rule-id.js";
import type { DraftedFixResult, ModelAssistInput } from "../../api/model.js";
import type {
  FindingView,
  HumanValidationInput,
  RemediationProposal,
  ValidationOutcome,
} from "../../api/types.js";
import { Badge, ReplayBadge, SeverityBadge, StatusBadge } from "../../components/Badge.js";
import { formatDateTime, formatPercent, shortHash, titleCase } from "../../lib/format.js";
import {
  INVESTIGATION_QUESTIONS,
  UNCONFIGURED_MODEL_ASSIST_MESSAGE,
} from "./investigation-questions.js";
import { ModelDraftedFixPanel } from "./ModelDraftedFix.js";
import { ModelExplanationPanel } from "./ModelExplanation.js";
import { ANALYSIS_FAMILY_LABELS } from "./model.js";
import { modelsForProvider } from "./model-catalog.js";
import { FindingAttackStory } from "./FindingAttackStory.js";
import { codeBehaviorNarrative, functionalImpactNarrative, impactNarrative } from "./narrative.js";
import { explainedSourceLines } from "./source-context.js";
import { findingStandards } from "./standards.js";
import "./finding-story.css";

function NumberedContent({ items, empty }: { items: readonly string[]; empty: string }) {
  return items.length > 0 ? (
    <ol className="numbered-content">
      {items.map((item) => (
        <li key={item}>{item}</li>
      ))}
    </ol>
  ) : (
    <p className="muted-copy">{empty}</p>
  );
}

type DecisionSnapshotFinding = Pick<
  FindingView,
  "evidence" | "status" | "suppressed" | "validationDepth" | "validations"
>;

export interface FindingDecisionSnapshot {
  readonly action: string;
  readonly stateExplanation: string;
}

export function findingDecisionSnapshot(finding: DecisionSnapshotFinding): FindingDecisionSnapshot {
  if (finding.suppressed || finding.status === "suppressed") {
    return {
      action: "Inspect the suppression audit record before changing policy state.",
      stateExplanation:
        "Policy suppression hides the finding from the actionable queue; it does not refute the evidence.",
    };
  }
  if (finding.evidence.replayStatus === "mismatch") {
    return {
      action: "Investigate the snapshot drift and replay the exact cited evidence.",
      stateExplanation:
        "The stored evidence no longer matches replay, so a truth decision cannot rely on it.",
    };
  }
  if (["unavailable", "unverified"].includes(finding.evidence.replayStatus)) {
    return {
      action: "Restore or verify replayable snapshot evidence before confirmation.",
      stateExplanation: `Evidence replay is ${finding.evidence.replayStatus}; the cited behavior is not currently reproducible.`,
    };
  }
  if (finding.status === "needs_human") {
    const contradictions = new Set(
      finding.validations.flatMap((validation) => validation.contradictions),
    );
    const missingDecision =
      contradictions.size > 0
        ? `${contradictions.size} recorded contradiction${contradictions.size === 1 ? " requires" : "s require"} resolution.`
        : finding.validationDepth < 4
          ? `The finding has reached ${finding.validationDepth}V but still requires an authorized 4V decision.`
          : "The recorded human decision keeps the claim open pending additional evidence.";
    return {
      action: "Resolve the missing corroboration or record an authorized human 4V decision.",
      stateExplanation: missingDecision,
    };
  }
  if (finding.status === "candidate") {
    return {
      action: "Obtain independent validation before treating this candidate as a finding.",
      stateExplanation: `The detector claim is replay-matched at ${finding.validationDepth}V but has not reached a canonical truth decision.`,
    };
  }
  if (finding.status === "refuted") {
    return {
      action:
        "Preserve the refutation evidence and reopen only if new evidence changes the decision.",
      stateExplanation: "An authorized validation event refuted the detector claim.",
    };
  }
  return {
    action: "Review the governed remediation path and verify the result with a new scan.",
    stateExplanation: `The replay-matched claim is confirmed at ${finding.validationDepth}V and remains actionable.`,
  };
}

export function PrivateFindingStatePanel({ findingId }: { findingId: string }) {
  const [loaded, setLoaded] = useState(false);
  const [pending, setPending] = useState(false);
  const [read, setRead] = useState(false);
  const [categories, setCategories] = useState("");
  const [message, setMessage] = useState("");

  const load = async () => {
    setPending(true);
    setMessage("");
    try {
      const response = await api.getFindingPrivateState(findingId);
      setRead(response.state?.read ?? false);
      setCategories(response.state?.categories.join(", ") ?? "");
      setLoaded(true);
    } catch {
      setMessage("Your private review state could not be loaded.");
    } finally {
      setPending(false);
    }
  };

  const save = async (event: FormEvent) => {
    event.preventDefault();
    setPending(true);
    setMessage("");
    try {
      const state = await api.updateFindingPrivateState(findingId, {
        read,
        categories: categories
          .split(",")
          .map((category) => category.trim())
          .filter(Boolean),
      });
      setRead(state.read);
      setCategories(state.categories.join(", "));
      setMessage("Private review state saved for your account.");
    } catch {
      setMessage("Your private review state could not be saved.");
    } finally {
      setPending(false);
    }
  };

  return (
    <section
      className="inspector-section inspector-section--neutral"
      aria-labelledby={`private-state-${findingId}`}
    >
      <div className="inspector-section__heading">
        <UserCheck aria-hidden="true" size={16} />
        <h3 id={`private-state-${findingId}`}>My private review state</h3>
      </div>
      {!loaded ? (
        <button
          className="button button--small"
          type="button"
          disabled={pending}
          onClick={() => void load()}
        >
          {pending ? "Loading…" : "Manage my state"}
        </button>
      ) : (
        <form className="private-state-form" onSubmit={(event) => void save(event)}>
          <label>
            <input
              type="checkbox"
              checked={read}
              onChange={(event) => setRead(event.target.checked)}
            />
            Marked read for me
          </label>
          <label className="form-field">
            <span>Private categories</span>
            <input
              value={categories}
              maxLength={1_300}
              placeholder="review today, auth flow"
              onChange={(event) => setCategories(event.target.value)}
            />
            <small>Comma-separated; visible only to your tenant principal.</small>
          </label>
          <button className="button button--small button--primary" type="submit" disabled={pending}>
            {pending ? "Saving…" : "Save private state"}
          </button>
        </form>
      )}
      {message ? (
        <p className="muted-copy" aria-live="polite">
          {message}
        </p>
      ) : null}
    </section>
  );
}

function parseSingleLineDiff(diff: string): { before: string; after: string } | null {
  const match = /^@@ -\d+,1 \+\d+,1 @@\n-(.*)\n\+(.*)$/su.exec(diff.trim());
  return match?.[1] !== undefined && match[2] !== undefined
    ? { before: match[1], after: match[2] }
    : null;
}

function AutoRemediationDiff({ proposal }: { proposal: RemediationProposal }) {
  const parsed = parseSingleLineDiff(proposal.previewDiff);
  return (
    <section className="auto-remediation-result" aria-label="Auto-remediation patch preview">
      <div className="auto-remediation-result__heading">
        <strong>Verified patch proposal</strong>
        <Badge
          tone={proposal.status === "ready" ? "positive" : "warning"}
          label={titleCase(proposal.status)}
        />
      </div>
      <p>{proposal.summary}</p>
      {parsed ? (
        <div className="auto-remediation-diff">
          <div>
            <span>Before · vulnerable behavior</span>
            <pre className="auto-remediation-diff__before">
              <code>{parsed.before}</code>
            </pre>
          </div>
          <div>
            <span>After · proposed change</span>
            <pre className="auto-remediation-diff__after">
              <code>{parsed.after}</code>
            </pre>
          </div>
        </div>
      ) : (
        <pre className="auto-remediation-diff__raw">
          <code>{proposal.previewDiff}</code>
        </pre>
      )}
      <p className="methodology-explainer">
        This is a reviewable proposal, not a source mutation. Approval and application remain
        separate governed actions, and the rollback plan is preserved with the proposal.
      </p>
    </section>
  );
}

function compactText(value: string | null | undefined, fallback: string, maxLength = 138): string {
  const normalized = value?.replace(/\s+/gu, " ").trim();
  if (!normalized) return fallback;
  if (normalized.length <= maxLength) return normalized;
  return `${normalized.slice(0, maxLength - 1).trimEnd()}…`;
}

function publicReviewActorLabel(
  actorType: "detector" | "validator" | "human",
  actorId: string | null | undefined,
  validatorId: string | null | undefined,
): string {
  const joined = `${actorId ?? ""} ${validatorId ?? ""}`.toLocaleLowerCase();
  if (actorType === "human") return "Named human reviewer";
  if (joined.includes("codex")) return "Independent AI security review";
  if (joined.includes("ollama") || joined.includes("model"))
    return "Evidence-bound assistant review";
  if (actorType === "detector") return "Native detector";
  return "Independent evidence validator";
}

function findingSurface(finding: FindingView): string {
  const text =
    `${finding.analysisFamily ?? ""} ${finding.ruleId} ${finding.title} ${finding.description} ${finding.location.path}`.toLocaleLowerCase();
  if (/(pqc|quantum|rsa|ecdsa|ed25519|kyber|dilithium|ml-kem|ml-dsa)/u.test(text)) {
    return "Long-lived cryptographic trust";
  }
  if (/(secret|credential|token|jwt|private key|password)/u.test(text)) {
    return "Credential and identity boundary";
  }
  if (/(sql|injection|xss|command|template)/u.test(text)) {
    return "Interpreter boundary";
  }
  if (/(auth|access|idor|bola|permission|tenant|admin)/u.test(text)) {
    return "Authorization boundary";
  }
  if (/(aws|iam|s3|role|terraform|cloud|bucket|security group)/u.test(text)) {
    return "Cloud control plane";
  }
  if (/(dependency|package|component|cve|ghsa|osv|sca)/u.test(text)) {
    return "Software supply chain";
  }
  if (/(container|image|docker|kubernetes|k8s)/u.test(text)) {
    return "Runtime image and workload";
  }
  return "Protected application behavior";
}

function findingBlob(finding: FindingView): string {
  return `${finding.ruleId} ${finding.title} ${finding.description} ${finding.impact} ${finding.evidence.redactedSnippet ?? ""} ${finding.cwe}`.toLocaleLowerCase();
}

export function cisoCharge(finding: FindingView): { name: string; sentence: string } {
  const text = findingBlob(finding);
  const cited = quotedCode(finding.evidence.redactedSnippet ?? "");
  if (/readobject|objectinputstream|deserial|cwe-502/u.test(text)) {
    return {
      name: "Unsafe Java deserialization",
      sentence: `Request bytes hit \`${cited || "ObjectInputStream.readObject()"}\`. Attacker language: gadget-chain RCE if a gadget is on the classpath. We have not run a gadget. The sink is real.`,
    };
  }
  if (/executequery|executeupdate|createstatement|jdbc|cwe-89|\bsql\b/u.test(text)) {
    return {
      name: "SQL injection",
      sentence: `\`${cited || "Statement.executeQuery"}\` is a JDBC interpreter sink. Attacker language: classic SQLi — tautology, UNION, stacked queries — if that string is request-built. We have not fired a payload. The sink is real.`,
    };
  }
  if (/innerhtml|document\.write|xss|cwe-79/u.test(text)) {
    return {
      name: "Cross-site scripting",
      sentence: `Untrusted markup reaches a browser sink (\`${cited}\`). Attacker language: stored or reflected XSS. Replay does not prove a browser executed it.`,
    };
  }
  if (/eval|new function|cwe-95|cwe-94/u.test(text)) {
    return {
      name: "Code injection",
      sentence: `\`${cited}\` interprets text as code. Attacker language: RCE at the language runtime. We have not executed the payload.`,
    };
  }
  if (/cwe-798|hardcoded|secret|credential|password|private key/u.test(text)) {
    return {
      name: "Embedded secret",
      sentence:
        "A reusable secret is in source. Attacker language: credential theft and replay. Rotate it. Do not treat a fingerprint as the secret.",
    };
  }
  if (/fail open|return true/u.test(text)) {
    return {
      name: "Fail-open authorization",
      sentence:
        "A failed check returns allow. Attacker language: knock the dependency over and walk in. This is not a timeout bug.",
    };
  }
  return {
    name: finding.title,
    sentence: `${cited ? `\`${cited}\` is the cited sink. ` : ""}Call it a defect until a named 4V decision says otherwise. Do not brief a breach you cannot cite.`,
  };
}

function exploitSketch(finding: FindingView, citedCode: string): string {
  const text =
    `${finding.ruleId} ${finding.title} ${finding.description} ${citedCode}`.toLocaleLowerCase();
  if (/(eval|function\s*\(|settimeout|setinterval|exec|spawn)/u.test(text)) {
    return "If request-controlled text reaches this call, the process may interpret attacker-supplied syntax instead of inert data.";
  }
  if (/(select|insert|update|delete|where|sql|query)/u.test(text)) {
    return "If request-controlled text is concatenated here, a query can be widened, bypassed, or altered before the database sees it.";
  }
  if (/(innerhtml|dangerouslysetinnerhtml|document\.write|xss)/u.test(text)) {
    return "If untrusted content lands here, the browser may treat it as markup or script instead of display text.";
  }
  if (/(rsa|ecdsa|ed25519|dh|ecdh|crypto|cipher|key)/u.test(text)) {
    return "This code is evidence of a crypto decision point; migration priority depends on key lifetime, protocol role, and exposed asset.";
  }
  if (/(password|secret|token|jwt|api[_-]?key|private)/u.test(text)) {
    return "This evidence can expose a reusable trust artifact; the safe path is rotation plus source and history cleanup.";
  }
  if (/(fail open|fails open|return true|catch)/u.test(text)) {
    return "If the authorization dependency throws, this catch returns allow instead of deny.";
  }
  return "The cited line is the proof anchor. The reviewer should validate the surrounding data-flow before making a final decision.";
}

function summaryAudienceCopy(
  finding: FindingView,
  decision: FindingDecisionSnapshot,
): { lead: string; owner: string } {
  const fix = finding.remediation[0]?.replace(/\s+/gu, " ").trim();
  if (finding.suppressed || finding.status === "suppressed") {
    return {
      lead: "Suppression hides this from the queue. It does not mean the weakness is gone.",
      owner: "Read the suppression rationale on Review before changing policy.",
    };
  }
  if (finding.status === "confirmed") {
    return {
      lead: "Confirmed. Assign an owner and a deadline before the next release.",
      owner: fix
        ? `In the cited line: ${fix} Then verify with a new scan.`
        : "Change the cited failure path so it denies access, then verify with a new scan.",
    };
  }
  if (finding.status === "needs_human" || finding.status === "candidate") {
    return {
      lead: "Not closed. A named human decision is still required.",
      owner: "Open Review, replay the cited line, then accept, keep open, or refute.",
    };
  }
  return {
    lead: decision.stateExplanation,
    owner: decision.action,
  };
}

function quotedCode(value: string): string {
  return value.replace(/\s+/gu, " ").trim() || "[Cited source unavailable]";
}

function citedFileName(path: string): string {
  const parts = path.split("/").filter(Boolean);
  return parts.at(-1) ?? path;
}

function citedLineFact(finding: FindingView, cited: string): string {
  const blob = `${finding.title} ${finding.description} ${cited}`.toLocaleLowerCase();
  if (/executequery|executeupdate|createstatement|jdbc|\bsql\b/u.test(blob)) {
    return "No PreparedStatement or bind parameter is visible on this line.";
  }
  if (/readobject|objectinputstream|deserial/u.test(blob)) {
    return "The stream names the type that gets instantiated.";
  }
  if (/innerhtml|document\.write|xss/u.test(blob)) {
    return "Untrusted input is written as markup, not encoded text.";
  }
  if (/eval|new function/u.test(blob)) {
    return "The runtime will interpret this text as code.";
  }
  if (/(tenant|idor|bola|authz|authoriz|permission|acl)\b/u.test(blob)) {
    return "No tenant or owner predicate is visible on this line.";
  }
  return "No fail-closed guard is visible on this line.";
}

export function developerActionCopy(finding: FindingView): {
  trigger: readonly string[];
  behavior: readonly string[];
  risk: readonly string[];
  fix: readonly string[];
} {
  const exhibit = sourceCodeExhibit(finding);
  const cited = quotedCode(exhibit.code);
  const fileRef = `${citedFileName(finding.location.path)}:${exhibit.line}`;
  const around = `${exhibit.before} ${cited} ${exhibit.after}`.toLocaleLowerCase();
  const blob = `${finding.title} ${finding.description} ${cited} ${around}`.toLocaleLowerCase();
  const entry = finding.reachability.entrypoint?.replace(/\(\)\s*$/u, "").trim();
  const rem = finding.remediation[0]?.replace(/\s+/gu, " ").trim();
  const failOpen = /(fail open|fails open|return true)/u.test(blob) && /catch/u.test(around);

  const trigger =
    failOpen && entry
      ? [
          `Entry is ${entry}().`,
          "Any caller can reach this catch.",
          "A throw (timeout, outage, bad input) is enough — no crafted exploit.",
        ]
      : entry
        ? [
            `Entry is ${entry}().`,
            "Callers of that function can execute this cited line.",
            "No extra guard is recorded above this line.",
          ]
        : [
            "No proven entry point is recorded.",
            "Treat any caller of this cited line as in-scope.",
            "Close reachability before calling this exploitable.",
          ];

  const behavior = failOpen
    ? ["The catch treats a failed check as allow.", `The cited token is \`${cited}\`.`]
    : [
        compactText(finding.description, "Detector description was not recorded.", 140),
        citedLineFact(finding, cited),
      ];

  const risk = failOpen
    ? [
        "A failed authorization check becomes an allow.",
        "Anyone who can make the dependency fail looks authorized.",
        "Actions that should be denied can still run.",
      ]
    : [
        compactText(
          finding.impact,
          "Impact is not yet recorded — confirm before treating this as closed.",
          140,
        ),
        compactText(
          finding.attackScenario.at(0),
          "No attack path is recorded beyond the cited line.",
          140,
        ),
      ];

  const fix = failOpen
    ? [
        `At ${fileRef}, do not return true in the catch.`,
        "Fail closed: return false (deny) or rethrow.",
        "Re-scan until this line no longer allows.",
      ]
    : rem
      ? [
          `At ${fileRef}, ${rem.replace(/^[A-Z]/u, (letter) => letter.toLocaleLowerCase())}`,
          "Keep the change fail-closed at this sink.",
          "Re-scan and confirm the cited range no longer matches.",
        ]
      : [
          `Change ${fileRef} so the unsafe behavior cannot execute.`,
          "Do not leave an allow/fail-open path.",
          "Re-scan and confirm the cited range no longer matches.",
        ];

  return { trigger, behavior, risk, fix };
}

function syntaxRole(explanation: string): string {
  return explanation.replace(/\s*Detector-cited line\.?\s*/giu, "").trim();
}

function forensicLineNote(line: { number: number; content: string; explanation: string }): string {
  const code = compactText(quotedCode(line.content), "[empty]", 56);
  const role = compactText(
    syntaxRole(line.explanation),
    "Syntax role is not certain from this line alone.",
    90,
  );
  return `L${line.number} \`${code}\` — ${role}`;
}

export function evidenceReadingCopy(finding: FindingView): {
  cited: readonly string[];
  neighbors: readonly string[];
  bound: readonly string[];
} {
  const exhibit = sourceCodeExhibit(finding);
  const lines = explainedSourceLines(finding);
  const citedLines = lines.filter((line) => line.cited);
  const citedCode = quotedCode(exhibit.code);
  const range =
    finding.location.lineStart === finding.location.lineEnd
      ? `L${finding.location.lineStart}`
      : `L${finding.location.lineStart}–${finding.location.lineEnd}`;
  const before = lines.filter((line) => !line.cited && line.number < finding.location.lineStart);
  const after = lines.filter((line) => !line.cited && line.number > finding.location.lineEnd);
  const lastBefore = before.at(-1);
  const firstAfter = after[0];

  const blob =
    `${citedCode} ${finding.title} ${finding.description} ${finding.ruleId}`.toLocaleLowerCase();
  const cited = /executequery|executeupdate|createstatement|jdbc|\bsql\b/u.test(blob)
    ? [
        `${range} is \`${citedCode}\`. That is a JDBC interpreter sink. If \`sql\` is concatenated, the database will parse attacker text as SQL, not as data.`,
        `No PreparedStatement or bind parameter is visible. This is a defect, not a style issue.`,
        `Rule ${displayRuleId(finding.ruleId)} cited bytes ${finding.location.byteStart}–${finding.location.byteEnd}. We have the token. We do not have the runtime value of \`sql\`. Do not invent a payload.`,
      ]
    : /readobject|objectinputstream|deserial/u.test(blob)
      ? [
          `${range} is \`${citedCode}\`. That is a Java deserialization sink. readObject instantiates whatever type the stream names.`,
          `If the stream is request-controlled, this is remote object instantiation, not a logging bug.`,
          `Rule ${displayRuleId(finding.ruleId)} cited bytes ${finding.location.byteStart}–${finding.location.byteEnd}. Replay matches the token. It does not execute a gadget chain.`,
        ]
      : [
          `${range} is \`${citedCode}\`. This is the detector-cited sink. Treat it as hostile until a named 4V decision says otherwise.`,
          citedLines.length > 1
            ? `${citedLines.length} stored cited lines. First token: \`${citedCode}\`.`
            : `Stored snippet: \`${citedCode}\`. No safe API is visible on this line.`,
          `Rule ${displayRuleId(finding.ruleId)} bound bytes ${finding.location.byteStart}–${finding.location.byteEnd}. Callee body and runtime inputs are not here.`,
        ];

  const neighborBrutal = (line: (typeof lines)[number]): string => {
    const code = quotedCode(line.content);
    if (/createStatement/u.test(code)) {
      return `L${line.number} \`${code}\` — opens a raw Statement. Wrong JDBC API for attacker-influenced text.`;
    }
    if (/ObjectInputStream|decodedstream/u.test(code)) {
      return `L${line.number} \`${code}\` — arms the deserializer. Not the cited call, but this is how the sink is loaded.`;
    }
    if (/result\.first|getString/u.test(code)) {
      return `L${line.number} \`${code}\` — runs after the query. Too late to make executeQuery safe.`;
    }
    return forensicLineNote(line);
  };

  const neighbors = [
    lastBefore ? neighborBrutal(lastBefore) : "No sealed line is stored before the citation.",
    firstAfter ? neighborBrutal(firstAfter) : "No sealed line is stored after the citation.",
    "Neighbors locate the citation. They are not the detector claim. Do not treat them as extra sinks.",
  ];

  const bound = [
    "This excerpt is frozen syntax. No runtime value, no live session, no exploit is in these bytes.",
    finding.evidence.replayStatus === "matched"
      ? "Replay matched snippet identity. That is not a green light to ship, and it is not proof someone already exploited it."
      : `Replay is ${finding.evidence.replayStatus}. Do not confirm a sink you cannot reproduce against this snapshot.`,
    "Bound to this snapshot. A later scan is a new finding. Do not reuse this evidence as proof against a different tree.",
  ];

  return { cited, neighbors, bound };
}

export function evidenceDeveloperFacts(finding: FindingView): readonly {
  label: string;
  value: string;
}[] {
  const cited = quotedCode(finding.evidence.redactedSnippet ?? "");
  const blob = `${cited} ${finding.ruleId} ${finding.title}`.toLocaleLowerCase();
  const safeApi = /executequery|jdbc|\bsql\b/u.test(blob)
    ? "PreparedStatement with bound parameters. Never concatenate into Statement.executeQuery."
    : /readobject|objectinputstream|deserial/u.test(blob)
      ? "Do not deserialize untrusted bytes. Use a type allowlist / ObjectInputFilter, or stop using Java serialization here."
      : /innerhtml|xss/u.test(blob)
        ? "Context-aware encoder or textContent. Do not assign untrusted HTML."
        : "Fail-closed API at this sink. Do not leave an interpreter of attacker text.";
  return [
    { label: "Cited token", value: cited },
    {
      label: "Location",
      value: `${finding.location.path}:${finding.location.lineStart} · bytes ${finding.location.byteStart}–${finding.location.byteEnd} · cols ${finding.location.columnStart}–${finding.location.columnEnd}`,
    },
    { label: "Replace with", value: safeApi },
    {
      label: "Do not",
      value:
        "Do not log the payload. Do not execute this file. Do not treat replay as a live exploit.",
    },
  ];
}

export function evidenceTrailCopy(finding: FindingView): {
  replay: string;
  custody: string;
  signal: string;
  disposition: string;
  standards: string;
} {
  const finder = finding.validations.find((event) => event.actorType === "detector");
  const finderLabel = finder
    ? `${publicReviewActorLabel(finder.actorType, finder.actorId, finder.validatorId)} ${finder.validatorVersion}`
    : "The detector event is not recorded";
  const replay =
    finding.evidence.replayStatus === "matched"
      ? "Replay matched the frozen snippet on this snapshot. That proves identity, not exploitability."
      : `Replay is ${finding.evidence.replayStatus}. Do not treat the citation as currently reproducible.`;
  const custody = `${finderLabel} cited ${displayRuleId(finding.ruleId)} @ ${finding.ruleVersion} as ${finding.cwe} / ${finding.owasp}.`;
  const signal = `Confidence ${formatPercent(finding.confidence)} is detector support for the claim, not exploitability. Quality ${formatPercent(finding.evidenceQuality)} is completeness of location, hashes, and replay.`;
  const disposition = finding.suppressed
    ? `Policy suppression is hiding this from the queue. Underlying truth remains ${titleCase(finding.truthStatus)}; suppression is not refutation.`
    : finding.status === "needs_human"
      ? `Disposition is Needs Human. A named 4V decision is still required.`
      : `Disposition is ${titleCase(finding.status)} at ${finding.validationDepth}V.`;
  return {
    replay,
    custody,
    signal,
    disposition,
    standards:
      "CWE names the weakness class. OWASP names the risk category. CVE, GHSA, and OSV identifiers are shown only when a signed component advisory names a concrete affected product version.",
  };
}

export function investigationBrief(finding: FindingView): {
  developer: readonly string[];
  expert: readonly string[];
  ciso: readonly string[];
  open: readonly string[];
} {
  const loc = `${finding.location.path}:${finding.location.lineStart}`;
  const action = developerActionCopy(finding);
  const cited = quotedCode(finding.evidence.redactedSnippet);
  const entry = finding.reachability.entrypoint?.trim();
  const developer = [
    `${loc} runs \`${cited}\`. ${action.fix[0] ?? "Change this sink so the unsafe call cannot execute."} Do not ship this.`,
    "Do not execute this repository to 'confirm' it. Patch, then re-scan the frozen fixture until the cited range is gone.",
  ];
  const expert = [
    finding.evidence.replayStatus === "matched"
      ? `Replay matched bytes at ${loc}. That is identity, not a live exploit and not a pass.`
      : `Replay is ${finding.evidence.replayStatus}. You cannot confirm a sink you cannot reproduce.`,
    finding.reachability.reachable === true && entry
      ? `Sealed path names ${entry}. Missing counter-evidence is not a pass.`
      : "Reachability is not demonstrated. Do not brief this as an attacker-ready route.",
  ];
  const ciso = [
    compactText(
      finding.impact,
      "Impact is not recorded — do not brief a breach you cannot cite.",
      140,
    ),
    finding.assignment
      ? `Owner ${finding.assignment.assigneeId} still owes a named 4V decision.`
      : "Unassigned. This is not tracked work until someone is named.",
  ];
  const open = [
    finding.evidence.replayStatus === "matched"
      ? "Bytes match. A named 4V decision is still missing."
      : `Replay is ${finding.evidence.replayStatus}. Restore identity before any confirmation.`,
    "Static only. Do not run the scanned source.",
  ];
  return { developer, expert, ciso, open };
}

export function reviewPacket(finding: FindingView): {
  code: string;
  impact: string;
  change: string;
  accept: string;
  keep: string;
  refute: string;
} {
  const loc = `${finding.location.path}:${finding.location.lineStart}`;
  const rem = finding.remediation[0]?.replace(/\s+/gu, " ").trim();
  return {
    code: `At ${loc}, ${compactText(finding.description, "the cited behavior is recorded only by the detector title.", 160)} Replay is ${finding.evidence.replayStatus} against this snapshot.`,
    impact: compactText(
      finding.impact,
      "Impact is not yet recorded — do not treat this as closed.",
      180,
    ),
    change: rem
      ? `${rem} Generate a governed patch from Remediation; it never writes the repository.`
      : "No deterministic remediation is recorded. Do not invent a patch on this tab.",
    accept: "Accept means the claim is true on this frozen snapshot and remains actionable.",
    keep: "Keep open means a named human still lacks corroboration or a closed assumption.",
    refute:
      "Refute means the claim is false; keep the evidence. Suppression is a separate policy action and does not refute.",
  };
}

function sourceCodeExhibit(finding: FindingView): {
  line: number;
  code: string;
  before: string;
  after: string;
  rows: readonly {
    number: number | null;
    content: string;
    role: "before" | "cited" | "after";
  }[];
} {
  const stored = new Map(explainedSourceLines(finding).map((line) => [line.number, line]));
  const citedLine = finding.location.lineStart;
  const cited = stored.get(citedLine) ?? {
    number: citedLine,
    content: finding.evidence.redactedSnippet || "[Cited source unavailable]",
    cited: true,
  };
  const rows = [-2, -1, 0, 1, 2].map((offset) => {
    const number = citedLine + offset;
    const line = number === citedLine ? cited : stored.get(number);
    return {
      number: number < 1 ? null : number,
      content: number < 1 ? "" : line?.content || " ",
      role: (offset < 0 ? "before" : offset > 0 ? "after" : "cited") as
        | "before"
        | "cited"
        | "after",
    };
  });
  const neighborIndent =
    rows
      .find((row) => row.role === "before" && /^\s+/u.test(row.content))
      ?.content.match(/^[ \t]*/u)?.[0] ??
    rows
      .find((row) => row.role === "after" && /^\s+/u.test(row.content))
      ?.content.match(/^[ \t]*/u)?.[0] ??
    "";
  const aligned = rows.map((row) =>
    row.role === "cited" && neighborIndent && !/^[ \t]/u.test(row.content)
      ? { ...row, content: `${neighborIndent}${row.content}` }
      : row,
  );
  return {
    line: cited.number,
    code: cited.content || "[Blank cited line]",
    before: aligned
      .filter((row) => row.role === "before")
      .map((row) => row.content)
      .join("\n"),
    after: aligned
      .filter((row) => row.role === "after")
      .map((row) => row.content)
      .join("\n"),
    rows: aligned,
  };
}

const STAGE_ROLE: Record<number, string> = {
  1: "Native detector",
  2: "Independent source replay",
  3: "Independent category review",
  4: "Named human reviewer",
};

export function verificationRungs(finding: FindingView): readonly {
  stage: number;
  who: string;
  result: string;
  current: boolean;
}[] {
  const last = Math.max(
    finding.validationDepth,
    ...finding.validations.map((event) => event.stage),
    1,
  );
  return Array.from({ length: last }, (_, index) => {
    const stage = (index + 1) as 1 | 2 | 3 | 4;
    const event = finding.validations.find((item) => item.stage === stage);
    return {
      stage,
      who: event
        ? publicReviewActorLabel(event.actorType, event.actorId, event.validatorId)
        : (STAGE_ROLE[stage] ?? `Stage ${stage}`),
      result: event
        ? titleCase(event.outcome)
        : stage <= finding.validationDepth
          ? "Counted"
          : "Not yet",
      current: stage === finding.validationDepth,
    };
  });
}

function dangerousSinkLabel(finding: FindingView, citedCode: string): string {
  const text = `${finding.ruleId} ${finding.title} ${finding.description} ${citedCode}`;
  if (/new\s+Function\s*\(/u.test(text)) return "new Function(...) executes constructed code";
  if (/\beval\s*\(/u.test(text)) return "eval(...) interprets attacker-controlled text";
  if (/\b(exec|spawn|execFile)\s*\(/u.test(text)) return "process execution sink";
  if (/(select|insert|update|delete).*(\+|template|string)/iu.test(text))
    return "dynamic SQL query construction";
  if (/(innerHTML|dangerouslySetInnerHTML|document\.write)/u.test(text))
    return "browser markup/script sink";
  return finding.title;
}

function hasIndependentAiReviewSignal(finding: FindingView): boolean {
  const text = [
    finding.ruleId,
    finding.sourceMetadata?.engineId,
    finding.sourceMetadata?.source,
    ...(finding.externalReferences ?? []).map((reference) => reference.system),
    ...finding.validations.flatMap((event) => [event.actorId, event.validatorId]),
  ]
    .filter(Boolean)
    .join(" ")
    .toLocaleLowerCase();
  return text.includes("codex");
}

function AttackPathStory({ finding }: { finding: FindingView }) {
  const severityClass = `attack-path-story--${finding.severity}`;
  const exhibit = sourceCodeExhibit(finding);
  const action = developerActionCopy(finding);
  const proofState =
    finding.evidence.replayStatus === "matched"
      ? "Replayed against frozen source"
      : `Replay ${finding.evidence.replayStatus}`;
  const exhibitFile = `${citedFileName(finding.location.path)}:${exhibit.line}`;
  return (
    <section
      className={`attack-path-story ${severityClass}`}
      aria-label="Visual attack path story"
      data-testid="attack-path-story"
    >
      <div className="attack-path-story__header">
        <span className="eyebrow">Threat story</span>
      </div>
      <div className="attack-path-story__so-what">
        <span>Why it matters</span>
        <p>{cisoCharge(finding).sentence}</p>
      </div>

      <div className="attack-path-story__canvas">
        <article className="exploit-code-exhibit">
          <div>
            <span>Cited line</span>
            <strong title={finding.location.path}>{exhibitFile}</strong>
          </div>
          <pre>
            <code>
              {exhibit.rows.map((row, index) => (
                <span
                  className={`exploit-code-exhibit__row exploit-code-exhibit__${row.role}`}
                  key={`${row.role}-${row.number ?? index}`}
                >
                  <span className="exploit-code-exhibit__line">{row.number ?? "··"}</span>
                  <span className="exploit-code-exhibit__src">{row.content}</span>
                </span>
              ))}
            </code>
          </pre>
        </article>

        <ol className="attack-path-story__chain">
          <li data-step="1">
            <span>01</span>
            <div>
              <strong>How you get here</strong>
              <ul className="attack-path-story__bullets">
                {action.trigger.map((item) => (
                  <li key={item}>{item}</li>
                ))}
              </ul>
            </div>
          </li>
          <li data-step="2">
            <span>02</span>
            <div>
              <strong>What this line does</strong>
              <ul className="attack-path-story__bullets">
                {action.behavior.map((item) => (
                  <li key={item}>{item}</li>
                ))}
              </ul>
            </div>
          </li>
          <li data-step="3">
            <span>03</span>
            <div>
              <strong>If you ship it</strong>
              <ul className="attack-path-story__bullets">
                {action.risk.map((item) => (
                  <li key={item}>{item}</li>
                ))}
              </ul>
            </div>
          </li>
          <li data-step="4">
            <span>04</span>
            <div>
              <strong>Change this</strong>
              <ul className="attack-path-story__bullets">
                {action.fix.map((item) => (
                  <li key={item}>{item}</li>
                ))}
              </ul>
            </div>
          </li>
        </ol>
      </div>

      <div className="attack-path-story__proof">
        <span>
          <Fingerprint aria-hidden="true" size={13} />
          {proofState}
        </span>
        <span>
          <ShieldCheck aria-hidden="true" size={13} />
          {titleCase(finding.status)}
        </span>
        <span>{Math.round(finding.confidence * 100)}% signal confidence</span>
        <span>{Math.round(finding.evidenceQuality * 100)}% evidence quality</span>
        {hasIndependentAiReviewSignal(finding) ? <span>Independent AI review signal</span> : null}
      </div>
    </section>
  );
}

function sealedLedgerRows(finding: FindingView): readonly {
  number: number | null;
  content: string;
  cited: boolean;
  explanation: string;
}[] {
  const exhibit = sourceCodeExhibit(finding);
  const stored = new Map(explainedSourceLines(finding).map((line) => [line.number, line]));
  return exhibit.rows.map((row) => {
    const line = row.number === null ? undefined : stored.get(row.number);
    return {
      number: row.number,
      content: row.content,
      cited: row.role === "cited",
      explanation:
        line?.explanation ??
        (row.role === "cited"
          ? "Detector-cited line."
          : "No sealed syntax is stored for this neighbor."),
    };
  });
}

function CodeEvidence({ finding }: { finding: FindingView }) {
  const evidence = finding.evidence;
  const exhibit = sourceCodeExhibit(finding);
  const ledger = sealedLedgerRows(finding);
  const reading = evidenceReadingCopy(finding);
  const beforeCount = exhibit.rows.filter((row) => row.role === "before").length;
  const afterCount = exhibit.rows.filter((row) => row.role === "after").length;
  const returnHash = `#source-${finding.id}-L${finding.location.lineStart}`;
  const returnTo =
    typeof window === "undefined"
      ? `/findings${returnHash}`
      : `${window.location.pathname}${window.location.search}${returnHash}`;
  const internalSourceHref = `/api/v1/findings/${encodeURIComponent(finding.id)}/source?returnTo=${encodeURIComponent(returnTo)}#L${finding.location.lineStart}`;
  const sourceHref = finding.sourcePermalink ?? internalSourceHref;
  const hasImmutableOrigin = Boolean(finding.sourcePermalink);

  return (
    <section
      className="evidence-docket"
      aria-labelledby={`evidence-${finding.id}`}
      data-testid="evidence-docket"
    >
      <div className="evidence-docket__header">
        <div>
          <span className="eyebrow">Frozen source</span>
          <h3 id={`evidence-${finding.id}`}>Code evidence</h3>
        </div>
        <ReplayBadge status={evidence.replayStatus} />
      </div>
      <p className="evidence-docket__path">
        <a
          className="source-filename"
          href={sourceHref}
          target="_blank"
          rel="noreferrer"
          aria-label={`Open ${hasImmutableOrigin ? "immutable original" : "verified snapshot"} source ${finding.location.path} at highlighted lines ${finding.location.lineStart} through ${finding.location.lineEnd}`}
        >
          <code>{finding.location.path}</code>
        </a>
      </p>
      <p className="evidence-docket__meta">
        {finding.analysisFamily ? `${finding.analysisFamily} · ` : ""}
        lines {finding.location.lineStart}–{finding.location.lineEnd} · bytes{" "}
        {finding.location.byteStart}–{finding.location.byteEnd}
      </p>
      <a className="source-permalink" href={sourceHref} target="_blank" rel="noreferrer">
        <ExternalLink aria-hidden="true" size={15} />
        {hasImmutableOrigin
          ? "Open immutable original at highlighted lines"
          : "Open verified source snapshot at highlighted lines"}
      </a>

      <article className="evidence-code-exhibit">
        <div>
          <span>Cited line</span>
          <strong>
            {finding.location.path}:{exhibit.line}
          </strong>
        </div>
        <pre>
          <code>
            {exhibit.rows.map((row, index) => (
              <span
                className={`evidence-code-exhibit__row evidence-code-exhibit__${row.role}`}
                id={
                  row.role === "cited" && row.number !== null
                    ? `source-${finding.id}-L${row.number}`
                    : undefined
                }
                key={`${row.role}-${row.number ?? index}`}
              >
                <span className="evidence-code-exhibit__line">{row.number ?? "··"}</span>
                <span className="evidence-code-exhibit__src">{row.content}</span>
              </span>
            ))}
          </code>
        </pre>
      </article>

      <p className="evidence-docket__lead">
        Sealed artifact and trust bounds. The action story and fix live on Summary.
      </p>
      <ol className="evidence-docket__chain">
        <li data-step="1">
          <span>01</span>
          <div>
            <strong>Cited line</strong>
            <ul className="evidence-docket__bullets">
              {reading.cited.map((item) => (
                <li key={item}>{item}</li>
              ))}
            </ul>
          </div>
        </li>
        <li data-step="2">
          <span>02</span>
          <div>
            <strong>Neighbors</strong>
            <ul className="evidence-docket__bullets">
              {reading.neighbors.map((item) => (
                <li key={item}>{item}</li>
              ))}
            </ul>
          </div>
        </li>
        <li data-step="3">
          <span>03</span>
          <div>
            <strong>What this excerpt does not prove</strong>
            <ul className="evidence-docket__bullets">
              {reading.bound.map((item) => (
                <li key={item}>{item}</li>
              ))}
            </ul>
          </div>
        </li>
      </ol>
      <details className="code-walkthrough" open>
        <summary>
          <span>Sealed line ledger</span>
          <span className="code-walkthrough__scope">
            {ledger.length} lines · {beforeCount} before · {afterCount} after
          </span>
        </summary>
        <p className="code-walkthrough__intro">
          Explanations describe only the syntax visible in this frozen excerpt.
        </p>
        <ol className="code-walkthrough__list">
          {ledger.map((line, index) => (
            <li
              className={
                line.cited
                  ? "code-walkthrough__line code-walkthrough__line--cited"
                  : "code-walkthrough__line"
              }
              key={`${line.cited ? "cited" : "neighbor"}-${line.number ?? index}`}
            >
              <strong>{line.number ?? "··"}</strong>
              <code>{line.content || " "}</code>
              <p>
                {line.cited ? "Detector-cited line. " : null}
                {syntaxRole(line.explanation)}
              </p>
            </li>
          ))}
        </ol>
      </details>
      <div className="evidence-integrity" data-testid="replay-provenance">
        <Fingerprint aria-hidden="true" size={14} />
        <span>Replay {evidence.replayStatus}</span>
        <span>Snapshot {shortHash(finding.snapshotId, 12)}</span>
        <span>Snippet {shortHash(evidence.snippetSha256, 12)}</span>
        <span>Evidence {shortHash(evidence.evidenceSha256, 12)}</span>
        <span>File {shortHash(evidence.fileSha256, 10)}</span>
        {finding.sourceMetadata?.configurationHash ? (
          <span>Configuration {shortHash(finding.sourceMetadata.configurationHash, 12)}</span>
        ) : null}
        {evidence.secretFingerprint ? (
          <Badge label="Secret evidence redacted" tone="warning" />
        ) : null}
      </div>
    </section>
  );
}

function ValidationTrail({ finding }: { finding: FindingView }) {
  const validators = finding.validations.filter((event) => event.actorType !== "detector");
  return (
    <section className="validation-timeline" aria-label="Validation events">
      {validators.length > 0 ? (
        validators.map((event) => (
          <p className="validation-event" key={event.id}>
            <span className="validation-event__stage">{event.stage}V</span>
            <span>
              {event.stage}V{" "}
              {publicReviewActorLabel(event.actorType, event.actorId, event.validatorId)}{" "}
              {titleCase(event.outcome)}. {event.rationale} Policy {event.policyVersion},{" "}
              {formatDateTime(event.createdAt)}.
            </span>
          </p>
        ))
      ) : (
        <p className="muted-copy">No independent validator events are recorded.</p>
      )}
    </section>
  );
}

function Provenance({ finding }: { finding: FindingView }) {
  const trail = evidenceTrailCopy(finding);
  const standards = findingStandards(finding);
  const detector = finding.validations.find((event) => event.actorType === "detector");
  const finderLabel = detector
    ? `${publicReviewActorLabel(detector.actorType, detector.actorId, detector.validatorId)} · ${detector.validatorVersion}`
    : (finding.sourceMetadata?.engineId ?? "The detector event is not recorded");
  return (
    <section className="evidence-trail" aria-labelledby={`trail-${finding.id}`}>
      <div className="evidence-trail__header">
        <div>
          <span className="eyebrow">Defensibility</span>
          <h3 id={`trail-${finding.id}`}>Evidence trail</h3>
        </div>
        <ReplayBadge status={finding.evidence.replayStatus} />
      </div>
      <p className="evidence-trail__note">
        Defensibility trail, not a vendor scorecard. Source line, rule, replay, and human decision
        stay separate.
      </p>
      <dl className="evidence-trail__facts">
        <div>
          <dt>Rule</dt>
          <dd>
            <strong>
              {displayRuleId(finding.ruleId)} @ {finding.ruleVersion}
            </strong>
          </dd>
        </div>
        <div>
          <dt>Finder</dt>
          <dd>{finderLabel}</dd>
        </div>
        <div>
          <dt>Taxonomy</dt>
          <dd>
            <ul>
              {standards.map((reference) => (
                <li key={`${reference.system}:${reference.label}`}>
                  <a href={reference.url} target="_blank" rel="noreferrer">
                    <ExternalLink aria-hidden="true" size={14} />
                    {reference.label}
                  </a>
                </li>
              ))}
            </ul>
          </dd>
        </div>
        <div>
          <dt>Evidence confidence</dt>
          <dd>{formatPercent(finding.confidence)}</dd>
        </div>
        <div>
          <dt>Evidence quality</dt>
          <dd>{formatPercent(finding.evidenceQuality)}</dd>
        </div>
        <div>
          <dt>Disposition</dt>
          <dd>
            <StatusBadge status={finding.status} />
          </dd>
        </div>
        <div>
          <dt>Finding</dt>
          <dd>
            <code>{finding.id}</code>
          </dd>
        </div>
      </dl>
      <p className="evidence-trail__signal">{trail.signal}</p>
      <p className="evidence-trail__standards">{trail.standards}</p>
      <ValidationTrail finding={finding} />
    </section>
  );
}

function HumanValidation({
  finding,
  onValidate,
}: {
  finding: FindingView;
  onValidate: (input: HumanValidationInput) => Promise<void>;
}) {
  const rationaleId = useId();
  const [outcome, setOutcome] = useState<ValidationOutcome>("passed");
  const [rationale, setRationale] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    const trimmed = rationale.trim();
    if (trimmed.length < 12) {
      setError("Add a rationale of at least 12 characters.");
      return;
    }
    setSubmitting(true);
    setError(null);
    setSuccess(null);
    try {
      await onValidate({
        stage: 4,
        outcome,
        rationale: trimmed,
        policyVersion: "human-review-v1",
      });
      setRationale("");
      setSuccess("Human validation recorded. The finding is refreshing.");
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : "The validation could not be recorded.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section className="inspector-section inspector-section--human">
      <div className="inspector-section__heading">
        <UserCheck aria-hidden="true" size={16} />
        <h3>Human 4V review</h3>
        <Badge
          label={
            finding.validationDepth === 4 ? "4V recorded" : `Currently ${finding.validationDepth}V`
          }
          tone={finding.validationDepth === 4 ? "positive" : "warning"}
        />
      </div>
      <p className="methodology-explainer">
        4V is the final, named human decision after detector evidence, replay, and an independent
        challenge. Models cannot record it or change the finding&apos;s truth state.
      </p>
      <form className="human-review-form" onSubmit={submit}>
        <fieldset>
          <legend>Decision</legend>
          <div className="decision-control">
            {(
              [
                ["passed", "Accept claim"],
                ["needs_human", "Keep open"],
                ["failed", "Refute claim"],
              ] as const
            ).map(([value, label]) => (
              <label key={value}>
                <input
                  type="radio"
                  name={`outcome-${finding.id}`}
                  value={value}
                  checked={outcome === value}
                  onChange={() => setOutcome(value)}
                />
                <span>{label}</span>
              </label>
            ))}
          </div>
        </fieldset>
        <label className="form-field" htmlFor={rationaleId}>
          <span>Review rationale</span>
          <textarea
            id={rationaleId}
            rows={3}
            value={rationale}
            maxLength={2000}
            placeholder="State the evidence reviewed, assumptions accepted, and any remaining limits."
            onChange={(event) => setRationale(event.target.value)}
          />
          <small>{rationale.length}/2000 · policy human-review-v1</small>
        </label>
        <div className="human-review-form__footer">
          <p className="form-message" aria-live="polite">
            {error ? <span className="form-message--error">{error}</span> : success}
          </p>
          <button className="button button--primary" type="submit" disabled={submitting}>
            <ShieldCheck aria-hidden="true" size={15} />
            {submitting ? "Recording" : "Record 4V decision"}
          </button>
        </div>
      </form>
    </section>
  );
}

function SuppressionControl({
  finding,
  onSuppress,
}: {
  finding: FindingView;
  onSuppress: (suppressed: boolean, rationale: string) => Promise<void>;
}) {
  const rationaleId = useId();
  const [rationale, setRationale] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const targetSuppressed = !finding.suppressed;

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    const trimmed = rationale.trim();
    if (trimmed.length < 10) {
      setMessage("Add a rationale of at least 10 characters.");
      return;
    }
    setSubmitting(true);
    setMessage(null);
    try {
      await onSuppress(targetSuppressed, trimmed);
      setRationale("");
      setMessage(`${targetSuppressed ? "Suppression" : "Restoration"} recorded.`);
    } catch (reason) {
      setMessage(
        reason instanceof Error ? reason.message : "The disposition could not be recorded.",
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section className="inspector-section inspector-section--suppression">
      <div className="inspector-section__heading">
        <CircleAlert aria-hidden="true" size={16} />
        <h3>Suppression policy</h3>
        <Badge
          label={finding.suppressed ? "Suppressed" : "Active"}
          tone={finding.suppressed ? "muted" : "neutral"}
        />
      </div>
      <p>
        Suppression preserves the underlying truth status and evidence. It records a policy
        disposition; it does not refute the claim.
      </p>
      <p className="methodology-explainer">
        A rerun creates a new finding for its new frozen snapshot, so this suppression does not
        automatically carry forward. The rediscovered finding is reviewed on its own evidence.
      </p>
      <form className="suppression-form" onSubmit={submit}>
        <label className="form-field" htmlFor={rationaleId}>
          <span>{targetSuppressed ? "Suppression rationale" : "Restoration rationale"}</span>
          <textarea
            id={rationaleId}
            rows={2}
            maxLength={2000}
            value={rationale}
            placeholder="State the approved policy basis and scope."
            onChange={(event) => setRationale(event.target.value)}
          />
        </label>
        <div className="suppression-form__action">
          <span aria-live="polite">{message}</span>
          <button className="button" type="submit" disabled={submitting}>
            {submitting ? "Recording" : targetSuppressed ? "Suppress finding" : "Restore finding"}
          </button>
        </div>
      </form>
    </section>
  );
}

/**
 * Ask a configured model to explain this finding.
 *
 * Rendered only where there is something to ask: a deployment with neither a
 * credentialless local provider nor a configured remote provider, or a session
 * that is not this tenant's owner, sees no control at all rather than a button
 * that always fails. `providers` arriving empty is the ordinary no-model
 * deployment, not an error.
 *
 * The model id is typed per page and stored nowhere, which is the same answer
 * the provider settings form gives and for the same reason: `model_credentials`
 * holds custody, not preferences. The form says so rather than leaving an
 * operator to wonder where it went.
 *
 * Nothing here decides whether the answer is renderable. The reply goes to
 * `ModelExplanationPanel` exactly as it arrived, and that component validates it
 * and emits the marker; this one has no way to suppress either.
 */
function ModelAssistControls({
  providers,
  pending,
  message,
  onExplain,
  onDraftFix,
}: {
  providers: readonly { id: string; egress: "local" | "remote" }[];
  pending: boolean;
  message: string;
  onExplain?: ((input: ModelAssistInput) => void) | undefined;
  onDraftFix?: ((input: ModelAssistInput) => void) | undefined;
}) {
  const providerId = useId();
  const modelId = useId();
  const questionId = useId();
  const [provider, setProvider] = useState(providers[0]?.id ?? "");
  const selected = providers.find((entry) => entry.id === provider) ?? providers[0];
  const models = modelsForProvider(selected?.id ?? "");
  const [model, setModel] = useState(models[0]?.id ?? "");
  const [question, setQuestion] = useState<string>(INVESTIGATION_QUESTIONS[3]);
  const [unconfiguredMessage, setUnconfiguredMessage] = useState("");
  const changeProvider = (nextProvider: string) => {
    setProvider(nextProvider);
    setModel(modelsForProvider(nextProvider)[0]?.id ?? "");
  };
  const configured = selected !== undefined;
  return (
    <section className="inspector-section inspector-section--model-assist">
      <div className="inspector-section__heading">
        <Bot aria-hidden="true" size={16} />
        <h3>AI investigation workspace</h3>
        {!configured ? (
          <Badge label="Not configured" tone="warning" />
        ) : selected.egress === "remote" ? (
          <Badge label="Sends this finding's text off-host" tone="warning" />
        ) : (
          <Badge label="Zero egress" tone="neutral" />
        )}
      </div>
      <p className="model-assist__promise">
        Interrogate the evidence, expose uncertainty, and propose the next safe action. AI output is
        advisory: it cannot change the verdict or write to your repository.
      </p>
      {!configured ? (
        <p className="muted-copy">
          No model provider is configured. The helper stays visible so you can ask the eight
          evidence-specific questions; responses are advisory or unavailable until a local provider
          is added in Settings.
        </p>
      ) : null}
      {onExplain ? (
        <div className="model-assist__question">
          <label className="form-field" htmlFor={questionId}>
            <span>Your investigation question</span>
            <textarea
              id={questionId}
              rows={2}
              maxLength={500}
              value={question}
              onChange={(event) => setQuestion(event.target.value)}
            />
          </label>
          <fieldset className="model-assist__quick-questions">
            <legend className="sr-only">Suggested questions</legend>
            {INVESTIGATION_QUESTIONS.map((suggestion) => (
              <button
                className="model-assist__question-chip"
                key={suggestion}
                type="button"
                onClick={() => setQuestion(suggestion)}
              >
                {suggestion}
              </button>
            ))}
          </fieldset>
        </div>
      ) : null}
      <div className="model-assist__form">
        {configured ? (
          <>
            <label className="form-field model-assist__field" htmlFor={providerId}>
              <span>Provider</span>
              <select
                id={providerId}
                value={selected.id}
                onChange={(event) => changeProvider(event.target.value)}
              >
                {providers.map((entry) => (
                  <option key={entry.id} value={entry.id}>
                    {entry.id}
                  </option>
                ))}
              </select>
            </label>
            {models.length > 0 ? (
              <label className="form-field model-assist__field" htmlFor={modelId}>
                <span>Model</span>
                <select
                  id={modelId}
                  value={model}
                  onChange={(event) => setModel(event.target.value)}
                >
                  {models.map((entry) => (
                    <option key={entry.id} value={entry.id}>
                      {entry.label} — {entry.description}
                    </option>
                  ))}
                </select>
              </label>
            ) : (
              <label className="form-field model-assist__field" htmlFor={modelId}>
                <span>Model identifier</span>
                <input
                  id={modelId}
                  value={model}
                  maxLength={256}
                  placeholder="For example: qwen3:14b"
                  onChange={(event) => setModel(event.target.value)}
                />
              </label>
            )}
          </>
        ) : null}
        {onExplain ? (
          <button
            className="button"
            type="button"
            disabled={pending || (configured && model.trim().length === 0)}
            onClick={() => {
              if (!selected) {
                setUnconfiguredMessage(UNCONFIGURED_MODEL_ASSIST_MESSAGE);
                return;
              }
              onExplain({
                providerId: selected.id,
                model: model.trim(),
                ...(question.trim().length >= 8 ? { question: question.trim() } : {}),
              });
            }}
          >
            {pending ? "Investigating" : "Investigate with AI"}
          </button>
        ) : null}
        {onDraftFix ? (
          <button
            className="button button--ghost"
            type="button"
            disabled={pending || (configured && model.trim().length === 0)}
            onClick={() => {
              if (!selected) {
                setUnconfiguredMessage(UNCONFIGURED_MODEL_ASSIST_MESSAGE);
                return;
              }
              onDraftFix({ providerId: selected.id, model: model.trim() });
            }}
          >
            {pending ? "Asking" : "Draft a fix"}
          </button>
        ) : null}
      </div>
      <details className="model-assist__disclosure">
        <summary>Data and safety controls</summary>
        <p className="muted-copy">
          Investigation sends bounded finding metadata, the source path, and the scanner-redacted
          cited snippet. Drafting additionally sends the cited source line. Raw evidence and record
          IDs are excluded. The provider and model apply to this page only.
        </p>
      </details>
      <p className="muted-copy" aria-live="polite">
        {message || unconfiguredMessage}
      </p>
    </section>
  );
}

/**
 * `modelExplanation` is `unknown` and optional, and both are deliberate.
 *
 * Optional, because an explanation is not part of a finding: `FindingView` comes
 * from `packages/domain` and has no field for one, so there is no shape in which
 * prose could arrive attached to the record. It is passed beside the finding or
 * not at all.
 *
 * `unknown`, because it is a `model_artifacts` payload — untrusted content from
 * outside the trust boundary — and `ModelExplanationPanel` validates it before
 * rendering a character of it. Typing it as a parsed object here would move that
 * decision to whoever calls this component.
 */
export type FindingStoryTab = "summary" | "evidence" | "investigate" | "review";

const STORY_TABS = [
  { id: "summary", label: "Summary" },
  { id: "evidence", label: "Evidence" },
  { id: "investigate", label: "Investigate" },
  { id: "review", label: "Review" },
] as const satisfies readonly { id: FindingStoryTab; label: string }[];

export function FindingInspector({
  finding,
  modelExplanation,
  modelDraftedFix,
  modelDraftedFixDecision,
  modelDecisionPending = false,
  modelProviders,
  modelPending = false,
  modelMessage = "",
  onExplain,
  onDraftFix,
  autoRemediation,
  autoRemediationPending = false,
  autoRemediationMessage = "",
  onAutoRemediate,
  onDecideArtifact,
  onClose,
  onValidate,
  onSuppress,
  canRemediate = false,
  canReview = true,
  initialTab = "summary",
  relatedFindings = [],
  onOpenFinding,
}: {
  finding: FindingView;
  modelExplanation?: unknown;
  /**
   * Typed, unlike `modelExplanation`, and the difference is not an oversight. An
   * explanation is a `model_artifacts` payload whose shape a provider chose; a
   * drafted-fix result is an envelope this API builds, and its `outcome` field
   * is what the panel branches on. `ModelDraftedFixPanel` re-checks it anyway.
   */
  modelDraftedFix?: DraftedFixResult | undefined;
  modelDraftedFixDecision?: "accepted" | "rejected" | null | undefined;
  modelDecisionPending?: boolean;
  /** Active providers. Empty or absent still renders the workspace in an advisory unavailable state. */
  modelProviders?: readonly { id: string; egress: "local" | "remote" }[] | undefined;
  modelPending?: boolean;
  modelMessage?: string;
  onExplain?: ((input: ModelAssistInput) => void) | undefined;
  onDraftFix?: ((input: ModelAssistInput) => void) | undefined;
  autoRemediation?: RemediationProposal | undefined;
  autoRemediationPending?: boolean | undefined;
  autoRemediationMessage?: string | undefined;
  onAutoRemediate?: (() => void) | undefined;
  onDecideArtifact?: ((artifactId: string, decision: "accepted" | "rejected") => void) | undefined;
  onClose: () => void;
  onValidate: (input: HumanValidationInput) => Promise<void>;
  onSuppress: (suppressed: boolean, rationale: string) => Promise<void>;
  canRemediate?: boolean | undefined;
  canReview?: boolean | undefined;
  /** Test/story seam. Production omits it so the first paint is Summary. */
  initialTab?: FindingStoryTab | undefined;
  /** Same-snapshot findings used to compose the scan map. Never treated as a live exploit. */
  relatedFindings?: readonly FindingView[] | undefined;
  onOpenFinding?: ((id: string) => void) | undefined;
}) {
  const decision = findingDecisionSnapshot(finding);
  const audience = summaryAudienceCopy(finding, decision);
  const brief = investigationBrief(finding);
  const packet = reviewPacket(finding);
  const behavior = codeBehaviorNarrative(finding);
  const impactAnalysis = impactNarrative(finding);
  const functionalImpact = functionalImpactNarrative(finding);
  const rungs = verificationRungs(finding);
  const openRungs = rungs.filter((rung) => rung.result !== "Passed" && rung.result !== "Counted");
  const [tab, setTab] = useState<FindingStoryTab>(initialTab);
  useEffect(() => {
    if (finding.id) setTab("summary");
  }, [finding.id]);
  const familyLabel =
    finding.analysisFamily && finding.analysisFamily in ANALYSIS_FAMILY_LABELS
      ? ANALYSIS_FAMILY_LABELS[finding.analysisFamily]
      : "source";
  return (
    <div className="finding-story finding-inspector" id={`inspector-${finding.id}`}>
      <button className="finding-story__back" type="button" onClick={onClose}>
        Back to findings
      </button>
      <span className="page-kicker">{familyLabel} finding</span>
      <div className="finding-story__title-row">
        <h1>{finding.title}</h1>
        <div>
          <SeverityBadge severity={finding.severity} />
          <StatusBadge status={finding.status} />
          <Badge label={`${finding.validationDepth}V`} tone="neutral" />
          <ReplayBadge status={finding.evidence.replayStatus} />
        </div>
      </div>
      <p className="muted-copy">
        {finding.repositoryName} · {finding.location.path}:{finding.location.lineStart} ·{" "}
        {displayRuleId(finding.ruleId)} · {formatPercent(finding.confidence)} ·{" "}
        {finding.assignment?.assigneeId ?? "Unassigned"}
      </p>
      <div role="tablist" aria-label="Finding story sections">
        <button
          type="button"
          role="tab"
          aria-selected={tab === "summary"}
          data-testid="finding-story-tab-summary"
          onClick={() => setTab("summary")}
        >
          {STORY_TABS[0].label}
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={tab === "evidence"}
          data-testid="finding-story-tab-evidence"
          onClick={() => setTab("evidence")}
        >
          {STORY_TABS[1].label}
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={tab === "investigate"}
          data-testid="finding-story-tab-investigate"
          onClick={() => setTab("investigate")}
        >
          {STORY_TABS[2].label}
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={tab === "review"}
          data-testid="finding-story-tab-review"
          onClick={() => setTab("review")}
        >
          {STORY_TABS[3].label}
        </button>
      </div>

      {tab === "summary" ? (
        <>
          <AttackPathStory finding={finding} />
          <section
            className="decision-snapshot"
            aria-label="Finding decision snapshot"
            data-testid="finding-above-fold"
          >
            <article className="decision-snapshot__primary" data-testid="finding-next-action">
              <span>Reviewer move</span>
              <p>
                <b>Security lead.</b> {audience.lead}
              </p>
              <p>
                <b>Why it is open.</b>{" "}
                {openRungs.length > 0
                  ? openRungs
                      .map((rung) => `${rung.stage}V ${rung.who}: ${rung.result}`)
                      .join(" · ")
                  : "No remaining verifier is blocked."}
              </p>
            </article>
            <ol
              className="summary-rungs"
              aria-label="Who verified this"
              title={rungs.map((rung) => `${rung.stage}V ${rung.who}: ${rung.result}`).join(" · ")}
            >
              {rungs.map((rung) => (
                <li key={rung.stage} data-current={rung.current ? "true" : undefined}>
                  <strong>
                    {rung.stage}V {rung.who}
                  </strong>
                  <span>{rung.result}</span>
                </li>
              ))}
            </ol>
            <div
              className="decision-snapshot__card decision-snapshot__patch"
              data-testid="finding-patch-availability"
            >
              <span>Patch</span>
              <p>
                {finding.hasDeterministicTemplate
                  ? "A deterministic template is registered. Generate it from Remediation — it never writes the repository."
                  : "No deterministic template. Do not invent a patch."}
              </p>
            </div>
            <div className="decision-snapshot__card decision-snapshot__owner">
              <span>Owner</span>
              <p>
                {finding.assignment
                  ? `${finding.assignment.assigneeId} · due ${formatDateTime(finding.assignment.dueAt)} · revision ${finding.assignment.revision}`
                  : "Unassigned — assign someone before treating this as tracked work."}
              </p>
            </div>
            <div className="decision-snapshot__card decision-snapshot__replay">
              <span>Replay</span>
              <p>
                <ReplayBadge status={finding.evidence.replayStatus} />{" "}
                {shortHash(finding.evidence.evidenceSha256, 12)}
              </p>
            </div>
          </section>
        </>
      ) : null}

      {tab === "evidence" ? (
        <>
          <CodeEvidence finding={finding} />
          <Provenance finding={finding} />
        </>
      ) : null}

      {tab === "investigate" ? (
        <>
          <FindingAttackStory
            finding={finding}
            related={relatedFindings}
            onOpenFinding={(id) => {
              if (id === finding.id) setTab("evidence");
              onOpenFinding?.(id);
            }}
          />
          <section className="investigation-brief" aria-label="Investigation brief">
            <div className="investigation-brief__header">
              <div>
                <span className="eyebrow">Three jobs, one finding</span>
                <h3>Investigation brief</h3>
              </div>
            </div>
            <p className="investigation-brief__lead">
              Developer changes the line. Security expert tests the claim. CISO owns residual risk.
              None of this executes scanned source.
            </p>
            <div className="investigation-brief__lanes">
              <article data-lane="developer">
                <span>Developer</span>
                <ul>
                  {brief.developer.map((item) => (
                    <li key={item}>{item}</li>
                  ))}
                </ul>
              </article>
              <article data-lane="expert">
                <span>Security expert</span>
                <ul>
                  {brief.expert.map((item) => (
                    <li key={item}>{item}</li>
                  ))}
                </ul>
              </article>
            </div>
            <p className="investigation-brief__ciso">
              <b>CISO.</b> {brief.ciso.join(" ")}
            </p>
            <div className="investigation-brief__open">
              <strong>Still open</strong>
              <ul>
                {brief.open.map((item) => (
                  <li key={item}>{item}</li>
                ))}
              </ul>
            </div>
          </section>
          <section
            className="inspector-section inspector-section--local-review"
            aria-label="Evidence-bound assistant review"
            data-testid="local-scan-review"
          >
            <div className="inspector-section__heading">
              <Bot aria-hidden="true" size={16} />
              <h3>Evidence-bound assistant review</h3>
            </div>
            <p className="methodology-explainer">
              Assistant analysis is a post-verdict review layer. It cannot confirm, refute,
              suppress, or change severity. Every sentence must stay tied to replayed evidence.
            </p>
            {modelExplanation ? (
              <ModelExplanationPanel explanation={modelExplanation} />
            ) : (
              <p className="muted-copy">
                No bounded assistant review is attached to this finding yet.
              </p>
            )}
          </section>
          {onExplain || onDraftFix ? (
            <ModelAssistControls
              providers={modelProviders ?? []}
              pending={modelPending}
              message={modelMessage}
              onExplain={onExplain}
              onDraftFix={onDraftFix}
            />
          ) : null}
          <ModelDraftedFixPanel
            finding={finding}
            result={modelDraftedFix}
            decision={modelDraftedFixDecision}
            decisionPending={modelDecisionPending}
            onDecide={onDecideArtifact}
          />
          <PrivateFindingStatePanel findingId={finding.id} />
          {finding.status === "confirmed" && finding.evidence.replayStatus !== "matched" ? (
            <p className="investigation-brief__warn">
              This confirmed label is not replay-complete and must not be treated as proven.
            </p>
          ) : null}
        </>
      ) : null}

      {tab === "review" ? (
        <>
          <div className="inspector-section-grid">
            <section className="inspector-section inspector-section--impact">
              <div className="inspector-section__heading">
                <Crosshair aria-hidden="true" size={16} />
                <h3>Impact details</h3>
              </div>
              <strong className="subsection-label">What the code currently does</strong>
              <NumberedContent
                items={[behavior.file, behavior.snippet]}
                empty="No code behavior was available."
              />
              <p className="section-lead">{finding.impact}</p>
              <strong className="subsection-label">Finding-specific security analysis</strong>
              <NumberedContent items={impactAnalysis} empty="No impact analysis was available." />
              <NumberedContent
                items={finding.attackScenario}
                empty="No attack sequence was recorded."
              />
              <strong className="subsection-label">Conditions required for this behavior</strong>
              <NumberedContent
                items={finding.prerequisites}
                empty="No prerequisites were recorded; treat reachability as unresolved."
              />
              <div className="reachability-note">
                <strong>Reachability</strong>
                <span>
                  {finding.reachability.reachable === null
                    ? "Undetermined"
                    : finding.reachability.reachable
                      ? "Reachable"
                      : "Not demonstrated"}
                  {finding.reachability.entrypoint ? ` · ${finding.reachability.entrypoint}` : ""}
                </span>
              </div>
            </section>
            <section className="inspector-section inspector-section--remediation">
              <div className="inspector-section__heading">
                <Wrench aria-hidden="true" size={16} />
                <h3>Change and functional impact</h3>
              </div>
              <strong className="subsection-label">Recommended changes</strong>
              <NumberedContent items={finding.remediation} empty="No remediation was recorded." />
              <strong className="subsection-label">Expected effect on functionality</strong>
              <NumberedContent
                items={functionalImpact}
                empty="No functional impact was available."
              />
              <strong className="subsection-label">Detection guidance</strong>
              <NumberedContent
                items={finding.detectionGuidance}
                empty="No detection guidance was recorded."
              />
              {canRemediate ? (
                <div className="auto-remediation-actions">
                  {finding.hasDeterministicTemplate ? (
                    <button
                      className="button button--primary"
                      type="button"
                      disabled={autoRemediationPending}
                      onClick={onAutoRemediate}
                    >
                      <Wrench aria-hidden="true" size={14} />
                      {autoRemediationPending ? "Generating patch…" : "Auto-generate remediation"}
                    </button>
                  ) : (
                    <p className="muted-copy">
                      This rule does not claim automatic support; no deterministic template is
                      registered. Advisory drafts remain available where a model provider is
                      configured.
                    </p>
                  )}
                  <a
                    className="button remediation-deep-link"
                    href={`/remediation?findingId=${encodeURIComponent(finding.id)}&repositoryId=${encodeURIComponent(finding.repositoryId)}`}
                  >
                    Open governed remediation
                  </a>
                </div>
              ) : null}
              {autoRemediationMessage ? (
                <p className="muted-copy" aria-live="polite">
                  {autoRemediationMessage}
                </p>
              ) : null}
              {autoRemediation ? <AutoRemediationDiff proposal={autoRemediation} /> : null}
            </section>
          </div>
          {canReview ? <HumanValidation finding={finding} onValidate={onValidate} /> : null}
          {canReview ? <SuppressionControl finding={finding} onSuppress={onSuppress} /> : null}
        </>
      ) : null}
    </div>
  );
}
